import dayjs from 'dayjs';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useStateRef from 'react-usestateref';
import { getCache, setCache } from '../utils/manager/cache-manager';

const StorageContext = createContext(null);

function StorageProvider({ children }) {
  const [menuId, _setMenuId, menuIdRef] = useStateRef(null);
  const [menuSlug, setMenuSlug, menuSlugRef] = useStateRef(null);
  const [menuLayout, _setMenuLayout, menuLayoutRef] = useStateRef(null);
  const [menuInfo, _setMenuInfo, menuInfoRef] = useStateRef(null);
  const [menuCategories, _setMenuCategories, menuCategoriesRef] =
    useStateRef(null);
  const [spot, _setSpot, spotRef] = useStateRef(null);
  const [orders, _setOrders, ordersRef] = useStateRef(null);
  const [languages, _setLanguages, languagesRef] = useStateRef(null);
  const [language, _setLanguage, languageRef] = useStateRef(null);
  const [payment, _setPayment, paymentRef] = useStateRef(null);
  const [paying, setPaying, payingRef] = useStateRef(false);
  const [user, _setUser, userRef] = useStateRef(null);
  const [session, _setSession] = useState(null);
  const [config, _setConfig, configRef] = useStateRef(null);
  const [experiment, _setExperiment] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [showingModal, setShowingModal] = useState(false);
  const [loadedSpot, setLoadedSpot] = useState(null);
  const [loadedCategory, setLoadedCategory] = useState(null);
  const [loadedItem, setLoadedItem] = useState(null);
  const [liveEditMode, setLiveEditMode] = useState();
  const [filter, setFilter] = useState();
  const [filterLoading, setFilterLoading, filterLoadingRef] =
    useStateRef(false);
  const [filterCount, setFilterCount] = useState();
  const userSetAtRef = useRef();

  useEffect(() => {
    loadStorage();
  }, []);

  function loadStorage() {
    _setMenuId(getCache(menuSlug, 'menu_id'));
    _setMenuLayout(getCache(menuSlug, 'menu_layout'));
    _setMenuInfo(getCache(menuSlug, 'menu_info'));
    _setMenuCategories(getCache(menuSlug, 'menu_categories'));

    _setSession(getCache(menuSlug, 'session'));
    _setPayment(getCache(menuSlug, 'payment'));
    _setOrders(getCache(menuSlug, 'orders'));
    _setLanguages(getCache(null, 'languages'));
    _setLanguage(getCache(menuSlug, 'language'));
    _setConfig(getCache(null, 'config') ?? {});
    _setExperiment(getCache(null, 'experiment'));

    _setUser(getCache(null, 'user'));
    _setSpot(getCache(menuSlug, 'spot'));
  }

  const setMenuId = useCallback(
    (pMenuId) => {
      setCache(menuSlug, 'menu_id', pMenuId);
      _setMenuId(pMenuId);
    },
    [menuSlug]
  );

  const setMenuLayout = useCallback(
    (pMenuLayout) => {
      setCache(menuSlug, 'menu_layout', pMenuLayout);
      _setMenuLayout(pMenuLayout);
    },
    [menuSlug]
  );

  const setMenuInfo = useCallback(
    (pMenuInfo) => {
      setCache(menuSlug, 'menu_info', pMenuInfo);
      _setMenuInfo(pMenuInfo);
    },
    [menuSlug]
  );

  const setMenuCategories = useCallback(
    (pMenuCategories) => {
      setCache(menuSlug, 'menu_categories', pMenuCategories);
      _setMenuCategories(pMenuCategories);
    },
    [menuSlug]
  );

  const setPayment = useCallback(
    (_payment) => {
      setCache(menuSlug, 'payment', _payment);
      _setPayment(_payment);
    },
    [menuSlug]
  );

  const setSession = useCallback(
    (_session) => {
      setCache(menuSlug, 'session', _session, {
        shelfLife: { value: 4, unit: 'h' },
      });
      _setSession(_session);
    },
    [menuSlug]
  );

  const setConfig = useCallback((_config) => {
    setCache(null, 'config', _config);
    _setConfig(_config ?? {});
  }, []);

  const setSpot = useCallback(
    (_spot) => {
      setCache(menuSlug, 'spot', _spot, { shelfLife: { value: 4, unit: 'h' } });
      _setSpot(_spot);
    },
    [menuSlug]
  );

  const setOrders = useCallback(
    (_orders) => {
      setCache(menuSlug, 'orders', _orders, {
        shelfLife: { value: 4, unit: 'h' },
      });
      _setOrders(_orders);
    },
    [menuSlug]
  );

  const setLanguages = useCallback((_languages) => {
    setCache(null, 'languages', _languages);
    _setLanguages(_languages);
  }, []);

  const setLanguage = useCallback(
    (_language) => {
      setCache(menuSlug, 'language', _language);
      _setLanguage(_language);
    },
    [menuSlug, languagesRef.current]
  );

  const setExperiment = useCallback((_experiment) => {
    setCache(null, 'experiment', _experiment, {
      shelfLife: { value: 7, unit: 'd' },
    });
    _setExperiment(_experiment);
  }, []);

  const setUser = useCallback((_user) => {
    setCache(null, 'user', _user);
    _setUser(_user);
    userSetAtRef.current = dayjs();
  }, []);

  const value = useMemo(
    () => ({
      loadStorage,

      menuId,
      setMenuId,
      menuIdRef,
      menuSlug,
      setMenuSlug,
      menuSlugRef,
      menuLayout,
      setMenuLayout,
      menuLayoutRef,
      menuInfo,
      setMenuInfo,
      menuInfoRef,
      menuCategories,
      setMenuCategories,
      menuCategoriesRef,

      spot,
      spotRef,
      setSpot,

      loadedSpot,
      setLoadedSpot,
      loadedCategory,
      setLoadedCategory,
      loadedItem,
      setLoadedItem,
      liveEditMode,
      setLiveEditMode,
      filter,
      setFilter,
      filterLoading,
      setFilterLoading,
      filterLoadingRef,
      filterCount,
      setFilterCount,

      orders,
      setOrders,
      ordersRef,
      languages,
      setLanguages,
      languagesRef,

      language,
      setLanguage,
      languageRef,

      session,
      setSession,
      payment,
      setPayment,
      paymentRef,

      paying,
      setPaying,
      payingRef,

      config,
      setConfig,
      configRef,
      user,
      userRef,
      setUser,
      userSetAtRef,
      experiment,
      setExperiment,
      origin,
      setOrigin,
      showingModal,
      setShowingModal,
    }),
    [
      loadStorage,

      menuId,
      setMenuId,
      menuIdRef,
      menuSlug,
      setMenuSlug,
      menuSlugRef,
      menuLayout,
      setMenuLayout,
      menuLayoutRef,
      menuInfo,
      setMenuInfo,
      menuInfoRef,
      menuCategories,
      setMenuCategories,
      menuCategoriesRef,

      spot,
      spotRef,
      setSpot,

      loadedSpot,
      setLoadedSpot,
      loadedCategory,
      setLoadedCategory,
      loadedItem,
      setLoadedItem,
      liveEditMode,
      setLiveEditMode,
      filter,
      setFilter,
      filterLoading,
      setFilterLoading,
      filterLoadingRef,
      filterCount,
      setFilterCount,

      orders,
      setOrders,
      ordersRef,
      session,
      setSession,
      payment,
      setPayment,
      paymentRef,

      paying,
      setPaying,
      payingRef,

      config,
      setConfig,
      configRef,

      user,
      userRef,
      setUser,
      userSetAtRef,
      experiment,
      setExperiment,
      origin,
      setOrigin,
      showingModal,
      setShowingModal,
    ]
  );

  return (
    <StorageContext.Provider value={value}>{children}</StorageContext.Provider>
  );
}

function useStorage(bypassContextError = false) {
  const context = useContext(StorageContext);

  if (!bypassContextError && !context) {
    throw new Error('useStorage must be used within an StorageProvider.');
  }

  return context;
}

export { StorageProvider, useStorage };
